import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listCurrentUserLicenses, toggleLicenseStatus } from '@/services/licenses';
import { listCurrentUserOrders } from '@/services/orders';
import type { License, Order } from '@/domain/types';
import { formatDate } from '@/lib/format';

export default function DashboardLicenses() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [userLicenses, setUserLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    listCurrentUserLicenses()
      .then((data) => { if (mounted) setUserLicenses(data); })
      .catch(() => { if (mounted) setUserLicenses([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    listCurrentUserLicenses()
      .then((data) => { if (mounted) setUserLicenses(data); })
      .catch(() => { if (mounted) setUserLicenses([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const handleToggle = async (lic: License) => {
    if (togglingId) return;
    setTogglingId(lic.id);
    setToggleError('');
    try {
      await toggleLicenseStatus(lic.id);
      setUserLicenses((prev) =>
        prev.map((l) =>
          l.id === lic.id
            ? { ...l, status: l.status === 'active' ? 'inactive' : 'active' }
            : l,
        ),
      );
    } catch (err: unknown) {
      setToggleError(err instanceof Error ? err.message : 'Failed to update license.');
    } finally {
      setTogglingId(null);
    }
  };

  const filtered = filter === 'all' ? userLicenses : userLicenses.filter((l) => l.status === filter);

  const statusColors: Record<string, string> = {
    active: 'bg-green-50 text-green-700',
    inactive: 'bg-surface-100 text-surface-600',
    expired: 'bg-amber-50 text-amber-700',
    disabled: 'bg-red-50 text-red-700',
    refunded: 'bg-red-50 text-red-700',
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-surface-900">My Licenses</h1>
          <p className="text-sm text-surface-500 mt-1">Manage your product licenses and activations.</p>
        </div>
        <div className="flex items-center gap-2">
          {['all', 'active', 'inactive', 'expired'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                filter === f
                  ? 'bg-brand-600 text-white'
                  : 'bg-white border border-surface-200 text-surface-600 hover:bg-surface-50'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {toggleError && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <i className="ri-error-warning-line" /> {toggleError}
        </p>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <i className="ri-loader-4-line animate-spin text-surface-400 text-xl" />
        </div>
      ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((license) => (
          <div key={license.id} className="bg-white rounded-xl border border-surface-200 p-5 hover:shadow-md transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-surface-900 text-sm">{license.productName}</h3>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColors[license.status] || 'bg-surface-100 text-surface-600'}`}>
                {license.status}
              </span>
            </div>
            <p className="text-xs text-surface-500 mb-1">{license.planName} Plan</p>
            {license.expiresAt && (
              <p className={`text-[10px] mb-2 ${new Date(license.expiresAt) < new Date() ? 'text-red-600 font-medium' : new Date(license.expiresAt) < new Date(Date.now() + 7*86400000) ? 'text-amber-600 font-medium' : 'text-surface-400'}`}>
                {new Date(license.expiresAt) < new Date() ? 'Expired' : `Expires ${formatDate(license.expiresAt)}`}
              </p>
            )}
            <p className="text-sm font-mono text-surface-700 mb-3">****-****-****-{license.licenseKeyLast4}</p>
            <div className="flex items-center justify-between text-xs text-surface-500 mb-2">
              <span>Sites used</span>
              <span>{license.activatedSites} / {license.siteLimit === 999 ? 'Unlimited' : license.siteLimit}</span>
            </div>
            <div className="h-1.5 bg-surface-200 rounded-full overflow-hidden mb-4">
              <div
                className="h-full bg-brand-500 rounded-full"
                style={{ width: license.siteLimit === 999 ? '10%' : `${(license.activatedSites / license.siteLimit) * 100}%` }}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-surface-400">{formatDate(license.createdAt)}</span>
              <div className="flex items-center gap-2">
                {license.status === 'active' && (
                  <button
                    onClick={() => handleToggle(license)}
                    disabled={togglingId === license.id}
                    className="px-3 py-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 text-xs font-medium transition-colors"
                  >
                    {togglingId === license.id ? <i className="ri-loader-4-line animate-spin" /> : 'Deactivate'}
                  </button>
                )}
                {license.status === 'inactive' && (
                  <span className="text-xs text-amber-600 font-medium">Pending payment</span>
                )}
                <button
                  onClick={() => navigate(`/dashboard/licenses/${license.id}`)}
                  className="px-3 py-1.5 rounded-lg bg-brand-50 text-brand-700 text-xs font-medium hover:bg-brand-100 transition-colors"
                >
                  Manage
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      )}


    </div>
  );
}